import { ColorBlock } from "@/components/colorblock/ColorBlock.tsx";
import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { LabelEditModal } from "@/pages/settings/recruit/labels/widgets/LabelEditModal.tsx";
import { useLabelDelete, useLabels } from "@/queries/use-labels.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiLabel } from "@/types/recruit/label.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  Stack,
  Table,
  TableCell,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function Labels() {
  const { t } = useTranslation();

  useTitle(t("ats:tags"), t("common:settings"));

  const { openModal } = useModals();
  const { data: labels, isLoading, isFetching } = useLabels();
  const labelDelete = useLabelDelete();

  const handleEditOrCreate = useCallback((label: ApiLabel | undefined = undefined) => {
    const labelToEdit = label || {
      id: 0,
      title: "",
      color: "brand-dark",
    };

    openModal(LabelEditModal, {
      label: labelToEdit,
    });
  }, []);

  const handleDelete = useCallback((label: ApiLabel) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await labelDelete.mutateAsync(label.id, {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.labels.deleted, {
                [analyticProperties.id]: label.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  const columns = useMemo<ColumnDef<ApiLabel>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => (
          <Stack direction={"horizontal"} className={"items-center"} gap={"sm"}>
            <ColorBlock color={row.row.original.color} />
            <TableCell>{row.row.original.title}</TableCell>
          </Stack>
        ),
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
    ],
    [labels]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.labels.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiLabel>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDelete(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleEditOrCreate, t]
  );

  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("ats:tags")}
          navigateTo={"/settings/recruitment-directories"}
        >
          <Stack>
            <Button
              onClick={() => {
                handleEditOrCreate();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={labels || []}
            columns={columns}
            showSkeleton={isLoading}
            isFetching={isFetching || labelDelete.isPending}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
